/**
 * ConsentFeed: the App Posture "Recent consents" list (§8.5) as a Carbon
 * structured list (DESIGN.md "Structured list", mockup 01 "Changes since last
 * week"): a 112px time column (date over the UTC time), the actor and the app
 * as the title, the scope and consent kind on the helper line, and the
 * highest permission tier as a severity glyph and word at the end. Only a
 * Critical tier takes a hue (the Shape-First Severity Rule).
 *
 * Consenting-actor attribution comes from `consentActor()` in
 * `src/lib/security-center/consent-actor.ts`, which handles the four
 * ConsentKind branches. Props and the empty-state copy are unchanged.
 */

import type { ConsentRecordRow } from './appPermissionsTypes'
import {
  consentActor,
  formatConsentTimestamp,
  type ConsentRecordRow as LocalConsentRecordRow,
} from '@/lib/security-center/consent-actor'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { Skeleton } from '@/components/carbon/Skeleton'
import { StructuredList } from '@/components/carbon/StructuredList'
import type { StructuredListRow } from '@/components/carbon/types'

/** The consent kind in words, for the helper line. */
function kindLabel(kind: ConsentRecordRow['consentKind']): string {
  switch (kind) {
    case 'UserDelegated':
      return 'User consent'
    case 'AdminDelegated':
    case 'AdminAppRole':
      return 'Admin consent'
    case 'AllPrincipals':
      return 'Org-wide consent'
  }
}

/** Take the first scope from a space-separated scope string and render an
 *  inline summary: "Mail.Read" / "Mail.Read (+2 more)". */
function scopeSummary(scope: string | null | undefined): {
  primary: string
  rest: number
} {
  if (!scope) return { primary: '—', rest: 0 }
  const tokens = scope
    .split(/\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  if (tokens.length === 0) return { primary: '—', rest: 0 }
  return { primary: tokens[0], rest: tokens.length - 1 }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

interface ConsentFeedProps {
  rows: ConsentRecordRow[] | undefined
  isLoading?: boolean
}

const EMPTY_STATE_COPY =
  'No new app consents in the last 30 days. Activity will surface here as it happens.'

export default function ConsentFeed({ rows, isLoading }: ConsentFeedProps) {
  if (isLoading) {
    return (
      <div className="border-t border-line-subtle py-4">
        <Skeleton lines={3} label="Loading the latest consent activity" />
      </div>
    )
  }

  const items: StructuredListRow[] = (rows ?? []).map(toListRow)

  return <StructuredList rows={items} aria-label="Recent app consents" empty={EMPTY_STATE_COPY} />
}

function toListRow(row: ConsentRecordRow): StructuredListRow {
  // The local type in consent-actor.ts is identical to the one re-exported
  // from appPermissionsTypes.ts, but TypeScript treats them nominally per
  // module. Cast through the helper-local shape since the structures match.
  const actor = consentActor(row as unknown as LocalConsentRecordRow)
  const stamp = formatConsentTimestamp(row.grantedAt)
  const { primary, rest } = scopeSummary(row.scope)

  return {
    id: row.id,
    time: (
      <>
        <span className="block">{stamp.date}</span>
        {stamp.time ? <span className="block">{stamp.time}</span> : null}
      </>
    ),
    title: (
      <>
        <b className="font-semibold">{actor}</b>
        <span className="text-text-secondary"> consented to </span>
        <b className="font-semibold">{row.appDisplayName}</b>
      </>
    ),
    sub: (
      <>
        <span className="break-all font-mono">{primary}</span>
        {rest > 0 ? ` and ${rest} more` : ''} · {kindLabel(row.consentKind)}
      </>
    ),
    tag: (
      <SeverityGlyph level={severityFromApi(row.highestPermissionTier)} size={12} className="text-helper">
        {row.highestPermissionTier}
      </SeverityGlyph>
    ),
  }
}
